import { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";

import customAxios from "../../../utils/customAxios";
import { cn } from "../../../utils/classnames";
import AdminPage from "../_components/AdminPage";
import AdminFilterBar, { MultiSelectField, SelectField } from "../_components/AdminFilterBar";
import { ChevronRightIcon, CloseIcon } from "../../icons";
import CalendarMonthGrid, { STATUS_BAR_COLORS } from "./_components/CalendarMonthGrid";
import CalendarBookingCard from "./_components/CalendarBookingCard";
import { EFFECTIVE_STATUS_LABELS, getEffectiveBookingStatus } from "./constants";

/**
 * Booking calendar.
 *
 * Reads the same schedule endpoint the driver and vehicle schedules use, with
 * no resource fixed, so every booking in the visible month comes back already
 * flattened per vehicle slot with its assigned vehicle and driver. The rows are
 * regrouped per booking here: one booking is one chip, however many cars it has.
 *
 * The month grid answers "what is on, and what still needs a driver"; the day
 * panel answers "who is driving what".
 */

const ASSIGNMENT_OPTIONS = [
  { value: "all", label: "All bookings" },
  { value: "unassigned", label: "Needs assignment" },
  { value: "assigned", label: "Fully assigned" },
];

// Values are the three states `getEffectiveBookingStatus` resolves to, spelled
// the way the admin knows them. No selection means no filter.
const STATUS_OPTIONS = [
  { value: "CONFIRMED", label: EFFECTIVE_STATUS_LABELS.CONFIRMED },
  { value: "CANCELLED", label: EFFECTIVE_STATUS_LABELS.CANCELLED },
  { value: "PENDING_CONFIRMATION", label: EFFECTIVE_STATUS_LABELS.PENDING },
];

const toEffectiveStatus = (optionValue) =>
  optionValue === "PENDING_CONFIRMATION" ? "PENDING" : optionValue;

/** Slot rows → one entry per booking, carrying every slot it owns. */
const groupRows = (rows) => {
  const byBooking = new Map();

  rows.forEach((row) => {
    const id = String(row._id);
    if (!byBooking.has(id)) {
      byBooking.set(id, {
        _id: id,
        key: row.key,
        cartID: row.cartID,
        dataVersion: row.dataVersion,
        bookingStatus: row.bookingStatus,
        paymentStatus: row.paymentStatus,
        bookingType: row.bookingType,
        isAirportTransfer: row.isAirportTransfer,
        pickupDateTime: row.pickupDateTime,
        dropoffDateTime: row.dropoffDateTime,
        pickupLocation: row.pickupLocation,
        destination: row.destination,
        customerName: row.customerName,
        slots: [],
      });
    }

    // A booking with no vehicles at all comes back as a single slotless row.
    if (row.vehicleCount > 0) {
      byBooking.get(id).slots.push({
        vehicleSlot: row.vehicleSlot,
        vehicleTypeName: row.vehicleTypeName,
        vehicleName: row.vehicleName,
        registrationNumber: row.registrationNumber,
        driverName: row.driverName,
        driverPhone: row.driverPhone,
        isAssigned: row.isAssigned,
      });
    }
  });

  return [...byBooking.values()]
    .map((booking) => ({
      ...booking,
      slots: booking.slots.sort((a, b) => a.vehicleSlot - b.vehicleSlot),
      hasUnassigned: !booking.slots.length || booking.slots.some((slot) => !slot.isAssigned),
      effectiveStatus: getEffectiveBookingStatus(booking),
    }))
    .sort((a, b) => moment(a.pickupDateTime).valueOf() - moment(b.pickupDateTime).valueOf());
};

const NavButton = ({ label, onClick, children }) => (
  <button
    type="button"
    aria-label={label}
    onClick={onClick}
    className="tw:flex tw:h-[38px] tw:w-[38px] tw:items-center tw:justify-center tw:rounded-[5px] tw:border tw:border-[#D9D9D9] tw:bg-white tw:p-0"
  >
    {children}
  </button>
);

const LegendDot = ({ color, children }) => (
  <span className="tw:flex tw:items-center tw:gap-[6px] tw:text-[13px] tw:!text-[#7C7B7B]">
    <span className="tw:block tw:h-[10px] tw:w-[3px] tw:rounded-full" style={{ backgroundColor: color }} />
    {children}
  </span>
);

const LegendDashed = ({ children }) => (
  <span className="tw:flex tw:items-center tw:gap-[6px] tw:text-[13px] tw:!text-[#7C7B7B]">
    <span className="tw:block tw:h-[14px] tw:w-[20px] tw:rounded-[3px] tw:border tw:border-dashed tw:border-[#C99A12] tw:bg-[#F7F7F7]" />
    {children}
  </span>
);

const BookingCalendar = () => {
  const [month, setMonth] = useState(() => moment().startOf("month"));
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  // The skeleton is for the first paint only. Later fetches keep the grid on
  // screen, so paging months (or pressing Today) never blanks the page.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState("");
  const [assignment, setAssignment] = useState(ASSIGNMENT_OPTIONS[0]);
  const [statuses, setStatuses] = useState([]);
  const [selectedDay, setSelectedDay] = useState(null);

  const fetchMonth = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      // The visible grid spills into the neighbouring months, so the window is
      // padded a week either side of the month rather than cut to it.
      const params = new URLSearchParams({
        startDate: month.clone().startOf("month").subtract(7, "days").toISOString(),
        endDate: month.clone().endOf("month").add(7, "days").toISOString(),
        includeCancelled: "true",
      });

      const response = await customAxios.get(`/api/admin/bookings/schedule/list?${params.toString()}`);
      setRows(response?.data?.data || []);
    } catch (err) {
      setError(err?.response?.data?.msg || "Could not load the calendar.");
      setRows([]);
    } finally {
      setLoading(false);
      setHasLoadedOnce(true);
    }
  }, [month]);

  useEffect(() => {
    fetchMonth();
  }, [fetchMonth]);

  // Escape closes the day panel, the way every other overlay on the admin behaves.
  useEffect(() => {
    if (!selectedDay) return undefined;
    const onKeyDown = (event) => {
      if (event.key === "Escape") setSelectedDay(null);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedDay]);

  const bookings = useMemo(() => groupRows(rows), [rows]);

  const filtered = useMemo(() => {
    // No status picked is "show all", so an empty selection filters nothing.
    const wantedStatuses = statuses.map((option) => toEffectiveStatus(option.value));

    return bookings.filter((booking) => {
      if (assignment.value === "unassigned" && !booking.hasUnassigned) return false;
      if (assignment.value === "assigned" && booking.hasUnassigned) return false;
      if (wantedStatuses.length && !wantedStatuses.includes(booking.effectiveStatus)) return false;
      return true;
    });
  }, [bookings, assignment, statuses]);

  const bookingsByDay = useMemo(() => {
    const map = new Map();
    filtered.forEach((booking) => {
      if (!booking.pickupDateTime) return;
      const key = moment(booking.pickupDateTime).format("YYYY-MM-DD");
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(booking);
    });
    return map;
  }, [filtered]);

  const inMonth = useMemo(
    () => filtered.filter((booking) => moment(booking.pickupDateTime).isSame(month, "month")),
    [filtered, month],
  );
  const unassignedInMonth = inMonth.filter((booking) => booking.hasUnassigned).length;

  const selectedBookings = selectedDay ? bookingsByDay.get(selectedDay) || [] : [];

  // Today is a no-op when the current month is already on screen — re-setting
  // the state would refetch and flash the grid for no change.
  const goToToday = () => {
    setSelectedDay(null);
    if (month.isSame(moment(), "month")) return;
    setMonth(moment().startOf("month"));
  };

  return (
    <AdminPage title="Booking Calendar">
      <div className="tw:mt-[24px] tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3">
        <div className="tw:flex tw:items-center tw:gap-2">
          <NavButton label="Previous month" onClick={() => setMonth(month.clone().subtract(1, "month"))}>
            <span className="tw:rotate-180">
              <ChevronRightIcon width={6} height={9} />
            </span>
          </NavButton>
          <NavButton label="Next month" onClick={() => setMonth(month.clone().add(1, "month"))}>
            <ChevronRightIcon width={6} height={9} />
          </NavButton>
          <button
            type="button"
            onClick={goToToday}
            className="tw:h-[38px] tw:rounded-[5px] tw:border tw:border-[#D9D9D9] tw:bg-white tw:px-4 tw:text-[14px] tw:!text-[#282828]"
          >
            Today
          </button>
          <h2 className="tw:mb-0 tw:ml-2 tw:!text-[20px] tw:!font-semibold tw:!leading-[normal] tw:!text-[#212529]">
            {month.locale("en").format("MMMM YYYY")}
          </h2>
        </div>

        <span className="tw:text-[14px] tw:!text-[#7C7B7B]">
          {loading
            ? "Loading…"
            : `${inMonth.length} booking${inMonth.length === 1 ? "" : "s"}` +
              (unassignedInMonth ? ` · ${unassignedInMonth} need assignment` : "")}
        </span>
      </div>

      <AdminFilterBar columns={4} collapsible={false}>
        <SelectField
          label="Assignment"
          value={assignment}
          onChange={(value) => setAssignment(value || ASSIGNMENT_OPTIONS[0])}
          options={ASSIGNMENT_OPTIONS}
        />
        <MultiSelectField
          label="Booking Status"
          value={statuses}
          onChange={(value) => setStatuses(value || [])}
          options={STATUS_OPTIONS}
          placeholder="All"
        />
      </AdminFilterBar>

      <div className="tw:mt-[14px] tw:flex tw:flex-wrap tw:items-center tw:gap-4">
        <LegendDot color={STATUS_BAR_COLORS.CONFIRMED}>{EFFECTIVE_STATUS_LABELS.CONFIRMED}</LegendDot>
        <LegendDot color={STATUS_BAR_COLORS.PENDING}>{EFFECTIVE_STATUS_LABELS.PENDING}</LegendDot>
        <LegendDot color={STATUS_BAR_COLORS.CANCELLED}>{EFFECTIVE_STATUS_LABELS.CANCELLED}</LegendDot>
        <LegendDashed>Needs vehicle &amp; driver</LegendDashed>
      </div>

      {error ? <div className="tw:mt-[16px] tw:text-[14px] tw:!text-[#EA4335]">{error}</div> : null}

      {loading && !hasLoadedOnce ? (
        <div className="tw:mt-[16px] tw:h-[640px] tw:w-full tw:animate-pulse tw:rounded-[5px] tw:bg-[#F3F3F3]" />
      ) : (
        // Seven readable columns need width, so on a narrow screen the grid
        // scrolls sideways rather than collapsing into unreadable slivers.
        <div className={cn("tw:overflow-x-auto", loading ? "tw:opacity-60" : "")}>
          <div className="tw:min-w-[860px]">
            <CalendarMonthGrid
              month={month}
              bookingsByDay={bookingsByDay}
              selectedDay={selectedDay}
              onSelectDay={setSelectedDay}
            />
          </div>
        </div>
      )}

      {selectedDay ? (
        <>
          <div
            className="tw:fixed tw:inset-0 tw:z-[1040] tw:bg-black/30"
            onClick={() => setSelectedDay(null)}
          />
          <aside className="tw:fixed tw:inset-y-0 tw:right-0 tw:z-[1050] tw:flex tw:w-full tw:max-w-[520px] tw:flex-col tw:bg-[#F7F7F7] tw:shadow-[0_0_30px_rgba(0,0,0,0.15)]">
            <header className="tw:flex tw:items-center tw:justify-between tw:gap-3 tw:border-b tw:border-[#E6E6E6] tw:bg-white tw:px-5 tw:py-4">
              <div>
                <div className="tw:text-[17px] tw:font-semibold tw:!text-[#282828]">
                  {moment(selectedDay).locale("en").format("dddd, D MMMM YYYY")}
                </div>
                <div className="tw:text-[13px] tw:!text-[#7C7B7B]">
                  {selectedBookings.length} booking{selectedBookings.length === 1 ? "" : "s"}
                </div>
              </div>
              <button
                type="button"
                aria-label="Close"
                onClick={() => setSelectedDay(null)}
                className="tw:flex tw:h-[32px] tw:w-[32px] tw:items-center tw:justify-center tw:rounded-full tw:border-0 tw:bg-[#F3F3F3]"
              >
                <CloseIcon />
              </button>
            </header>

            <div className="tw:flex tw:flex-1 tw:flex-col tw:gap-3 tw:overflow-y-auto tw:p-5">
              {selectedBookings.length ? (
                selectedBookings.map((booking) => (
                  <CalendarBookingCard key={booking._id} booking={booking} />
                ))
              ) : (
                <div className="tw:rounded-[5px] tw:border tw:border-[#E6E6E6] tw:bg-white tw:py-[40px] tw:text-center tw:text-[14px] tw:!text-[#7C7B7B]">
                  Nothing booked on this day.
                </div>
              )}
            </div>
          </aside>
        </>
      ) : null}
    </AdminPage>
  );
};

export default BookingCalendar;
